import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getAuth, applyActionCode, checkActionCode, verifyPasswordResetCode } from "firebase/auth";
import { X, CheckCircle, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import LogoImage from "../Images/orra_logo_reszie.png";

const EmailVerification = () => {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [emailInfo, setEmailInfo] = useState(null);
  const [actionType, setActionType] = useState("");
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAction = async () => {
      // Get the action code from the URL
      const urlParams = new URLSearchParams(location.search);
      const actionCode = urlParams.get('oobCode');
      const mode = urlParams.get('mode');

      console.log("URL Params:", { actionCode, mode });

      // Check if we have an action code
      if (!actionCode) {
        setError("Lien d'action manquant");
        setLoading(false);
        return;
      }

      // Handle different action types
      setActionType(mode);

      try {
        const auth = getAuth();
        
        if (!auth) {
          throw new Error("Firebase Auth n'est pas initialisé");
        }

        // Route to appropriate handler based on mode
        switch (mode) {
          case 'verifyEmail':
            await handleEmailVerification(auth, actionCode);
            break;
          
          case 'resetPassword':
            await handlePasswordReset(auth, actionCode);
            break;
          
          case 'recoverEmail':
            setError("La récupération d'email doit être gérée différemment.");
            break;
          
          default:
            setError(`Type d'action non reconnu: ${mode}`);
        }
        
      } catch (error) {
        console.error("Error handling action:", error);
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    const handleEmailVerification = async (auth, actionCode) => {
      console.log("Checking action code for email verification...");
      
      // Check what the action code is for
      const info = await checkActionCode(auth, actionCode);
      console.log("Action code info:", info);
      setEmailInfo(info);

      console.log("Applying action code...");
      
      // Apply the action code to complete email verification
      await applyActionCode(auth, actionCode);
      
      console.log("Email verified successfully!");
      
      setVerified(true);
      toast.success("Email vérifié avec succès !");
      
      // Auto-redirect after 5 seconds
      setTimeout(() => {
        navigate("/profile");
      }, 5000);
    };

    const handlePasswordReset = async (auth, actionCode) => {
      console.log("Verifying password reset code...");
      
      // Verify the password reset code is valid
      await verifyPasswordResetCode(auth, actionCode);
      
      console.log("Password reset code is valid, redirecting...");
      
      // Redirect to password reset page with the code
      navigate(`/password/create-new?oobCode=${actionCode}`);
    };

    const handleError = (error) => {
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      
      switch (error.code) {
        case 'auth/expired-action-code':
          setError("Le lien a expiré. Veuillez demander un nouveau lien.");
          break;
        case 'auth/invalid-action-code':
          setError("Le lien est invalide ou a déjà été utilisé.");
          break;
        case 'auth/user-disabled':
          setError("Ce compte utilisateur a été désactivé.");
          break;
        case 'auth/user-not-found':
          setError("Aucun utilisateur trouvé avec cette adresse email.");
          break;
        default:
          setError(`Erreur: ${error.message || "Veuillez réessayer."}`);
      }
    };

    handleAction();
  }, [location.search, navigate]);

  // Early return for missing action code
  const urlParams = new URLSearchParams(location.search);
  const actionCode = urlParams.get('oobCode');
  
  if (!actionCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-black">
        <div className="text-white text-center">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-xl mb-4">Lien d'action manquant</p>
          <p className="text-gray-400 mb-4">
            Ce lien semble être incomplet. Veuillez utiliser le lien complet reçu dans votre email.
          </p>
          <Link to="/profile" className="text-blue-400 hover:text-blue-300">
            Revenir au profil
          </Link>
        </div>
      </div>
    );
  }

  // Get title based on action type
  const getTitle = () => {
    switch (actionType) {
      case 'verifyEmail':
        return "Vérification d'email";
      case 'resetPassword':
        return "Réinitialisation du mot de passe";
      case 'recoverEmail':
        return "Récupération d'email";
      default:
        return "Action en cours";
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-gradient-to-br from-blue-900 to-black">
      <div className="mb-10 flex items-center gap-2 mt-10">
        <div className="flex flex-col text-white font-normal">
          <img src={LogoImage} width={180} height={95} alt="Logo" />
        </div>
      </div>
      
      <div className="w-[90%] md:max-w-[600px] h-fit md:h-[400px] px-8 py-6 border border-gray-800 bg-[#121937] rounded-xl shadow-lg">
        <div className="flex justify-end items-center gap-1 mb-6">
          <Link to="/profile" className="text-gray-300 hover:text-white flex items-center gap-1">
            <X />
            <span>Fermer</span>
          </Link>
        </div>
        
        <div className="text-center">
          <h2 className="text-2xl text-gray-200 font-bold mb-6">
            {getTitle()}
          </h2>
          
          {loading && (
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="text-gray-400">
                {actionType === 'resetPassword' 
                  ? "Vérification du lien de réinitialisation..." 
                  : "Vérification de votre email en cours..."}
              </p>
            </div>
          )}
          
          {verified && !loading && (
            <div className="flex flex-col items-center space-y-4">
              <CheckCircle className="text-green-500" size={64} />
              <h3 className="text-xl text-green-500 font-semibold">
                Email vérifié avec succès !
              </h3>
              {emailInfo?.data?.email && (
                <p className="text-gray-400">
                  L'adresse email <span className="text-white font-medium">{emailInfo.data.email}</span> a été vérifiée.
                </p>
              )}
              <p className="text-gray-400">
                Vous serez redirigé automatiquement vers votre profil dans 5 secondes...
              </p>
              <Link 
                to="/profile" 
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#2952F0] via-[#4268FF] to-[#113BE1] text-white rounded-lg hover:shadow-lg transition-all duration-200"
              >
                Continuer vers le profil
              </Link>
            </div>
          )}
          
          {error && !loading && (
            <div className="flex flex-col items-center space-y-4">
              <AlertCircle className="text-red-500" size={64} />
              <h3 className="text-xl text-red-500 font-semibold">
                Erreur de vérification
              </h3>
              <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
              <div className="space-y-2">
                <Link 
                  to="/profile" 
                  className="block px-6 py-3 bg-gradient-to-r from-[#2952F0] via-[#4268FF] to-[#113BE1] text-white rounded-lg hover:shadow-lg transition-all duration-200"
                >
                  Revenir au profil
                </Link>
                <p className="text-gray-400 text-sm">
                  Vous pouvez demander un nouveau lien depuis votre profil.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;